import { Capacitor, registerPlugin } from '@capacitor/core';

import { isLikelyNativeAppShell } from '@/lib/native-shell-detect';
import { openCapacitorAppSettings } from '@/lib/native-permissions-capacitor';

export type RuntimePermissionState = 'granted' | 'denied' | 'prompt';

export type RuntimePermissionCheck = {
  microphone: RuntimePermissionState;
  camera: RuntimePermissionState;
  photos: RuntimePermissionState;
  location: RuntimePermissionState;
  notifications: RuntimePermissionState;
};

export interface RuntimePermissionsPlugin {
  check(): Promise<RuntimePermissionCheck>;
  requestMicrophone(): Promise<{ state: RuntimePermissionState }>;
  requestCamera(): Promise<{ state: RuntimePermissionState }>;
  requestPhotos(): Promise<{ state: RuntimePermissionState }>;
  requestLocation(): Promise<{ state: RuntimePermissionState }>;
  requestNotifications(): Promise<{ state: RuntimePermissionState }>;
  openAppSettings(): Promise<void>;
}

const RuntimePermissions = registerPlugin<RuntimePermissionsPlugin>('RuntimePermissions');

/**
 * True only when the Android Java plugin can actually show a system dialog.
 * iOS has no RuntimePermissions implementation — JS registration is not enough,
 * and treating a missing plugin as `denied` sent reviewers to Settings (5.1.1(iv)).
 */
export function isRuntimePermissionsPluginAvailable(): boolean {
  if (!isLikelyNativeAppShell()) return false;
  try {
    if (Capacitor.getPlatform() === 'ios') return false;
  } catch {
    /* ignore */
  }
  try {
    if (typeof Capacitor.isPluginAvailable === 'function') {
      return Capacitor.isPluginAvailable('RuntimePermissions');
    }
  } catch {
    /* ignore */
  }
  try {
    return Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

export async function checkRuntimePermissions(): Promise<RuntimePermissionCheck | null> {
  if (!isRuntimePermissionsPluginAvailable()) return null;
  try {
    return await RuntimePermissions.check();
  } catch {
    return null;
  }
}

export async function requestRuntimeMicrophone(): Promise<RuntimePermissionState> {
  if (!isRuntimePermissionsPluginAvailable()) return 'denied';
  try {
    const res = await RuntimePermissions.requestMicrophone();
    return res.state;
  } catch {
    return 'denied';
  }
}

export async function requestRuntimeCamera(): Promise<RuntimePermissionState> {
  if (!isRuntimePermissionsPluginAvailable()) return 'denied';
  try {
    const res = await RuntimePermissions.requestCamera();
    return res.state;
  } catch {
    return 'denied';
  }
}

export async function requestRuntimePhotos(): Promise<RuntimePermissionState> {
  if (!isRuntimePermissionsPluginAvailable()) return 'denied';
  try {
    const res = await RuntimePermissions.requestPhotos();
    return res.state;
  } catch {
    return 'denied';
  }
}

export async function requestRuntimeLocation(): Promise<RuntimePermissionState> {
  if (!isRuntimePermissionsPluginAvailable()) return 'denied';
  try {
    const res = await RuntimePermissions.requestLocation();
    return res.state;
  } catch {
    return 'denied';
  }
}

export async function requestRuntimeNotifications(): Promise<RuntimePermissionState> {
  if (!isRuntimePermissionsPluginAvailable()) return 'granted';
  try {
    const res = await RuntimePermissions.requestNotifications();
    return res.state;
  } catch {
    return 'denied';
  }
}

export async function openNativeAppSettings(): Promise<void> {
  if (isRuntimePermissionsPluginAvailable()) {
    try {
      await RuntimePermissions.openAppSettings();
      return;
    } catch {
      /* fall through to iOS / Capacitor URL */
    }
  }
  await openCapacitorAppSettings();
}
